<template>
  <div class="section mt-1 pr-courses-page">
    <div class="page-container">
      <div class="card pr-filter-card">
        <div class="card-body">
          <button
            type="button"
            class="pr-filter-header"
            :class="{ 'mb-2': filtersOpen }"
            @click="filtersOpen = !filtersOpen"
          >
            <div class="pr-filter-title">
              <ion-icon name="options-outline"></ion-icon>
              <span>Filters</span>
            </div>
            <div class="pr-filter-chevron" :class="{ open: filtersOpen }">
              <ion-icon name="chevron-down-outline"></ion-icon>
            </div>
          </button>

          <div class="pr-filter-collapse" v-show="filtersOpen">
          <div class="row g-2 pr-filter-grid">
            <div class="col-12 col-md-6">
              <div class="form-group basic mb-0">
                <div class="input-wrapper">
                  <label class="label">Course</label>
                  <select class="form-control form-select" v-model="draftFilters.course_master_id">
                    <option value="">All courses</option>
                    <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.course_name }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-6">
              <div class="form-group basic mb-0">
                <div class="input-wrapper">
                  <label class="label">Madrasa</label>
                  <select class="form-control form-select" v-model="draftFilters.madrasa_id">
                    <option value="">All madrasas</option>
                    <option v-for="m in madrasas" :key="m.id" :value="m.id">{{ m.name }}</option>
                  </select>
                </div>
                <div class="small text-muted mt-1" v-if="madrasaLoading">Loading madrasas…</div>
              </div>
            </div>

            <div class="col-12 col-md-4" v-if="showDistrictSelect">
              <div class="form-group basic mb-0">
                <div class="input-wrapper">
                  <label class="label">District</label>
                  <select class="form-control form-select" v-model="draftFilters.district_id" @change="onDistrictChange">
                    <option value="">All districts</option>
                    <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-4" v-if="showZoneSelect">
              <div class="form-group basic mb-0">
                <div class="input-wrapper">
                  <label class="label">Zone</label>
                  <select
                    class="form-control form-select"
                    v-model="draftFilters.zone_id"
                    :disabled="!draftFilters.district_id || zoneLoading"
                    @change="onZoneChange"
                  >
                    <option value="">{{ !draftFilters.district_id ? "Select district first" : "All zones" }}</option>
                    <option v-for="z in zones" :key="z.id" :value="z.id">{{ z.name }}</option>
                  </select>
                </div>
                <div class="small text-muted mt-1" v-if="zoneLoading">Loading zones…</div>
              </div>
            </div>

            <div class="col-12 col-md-4" v-if="showUnitSelect">
              <div class="form-group basic mb-0">
                <div class="input-wrapper">
                  <label class="label">Unit</label>
                  <select
                    class="form-control form-select"
                    v-model="draftFilters.unit_id"
                    :disabled="!draftFilters.zone_id || unitLoading"
                    @change="onUnitChange"
                  >
                    <option value="">{{ !draftFilters.zone_id ? "Select zone first" : "All units" }}</option>
                    <option v-for="u in units" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                </div>
                <div class="small text-muted mt-1" v-if="unitLoading">Loading units…</div>
              </div>
            </div>

            <div class="col-12 col-md-8 d-none d-md-block"></div>
          </div>

          <div class="alert alert-danger py-2 px-3 small mt-2 mb-0" v-if="errorMessage">
            {{ errorMessage }}
          </div>
          </div>
        </div>

        <!-- Card footer actions (mobile + desktop) -->
        <div class="pr-filter-footer" v-show="filtersOpen">
          <button class="btn btn-outline-secondary" type="button" @click="resetFilters" :disabled="loading">
            Reset
          </button>
          <button class="btn btn-primary" type="button" @click="applyFilters" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            Apply
          </button>
        </div>
      </div>

      <!-- Search Box (same pattern as org executive lists) -->
      <div id="search" class="pr-search-outside">
        <form class="search-form" @submit.prevent>
          <div class="form-group searchbox">
            <input
              type="text"
              class="form-control"
              placeholder="Search"
              v-model="searchQuery"
              @input="onSearch"
              autocomplete="off"
            >
            <i class="input-icon">
              <ion-icon name="search-outline"></ion-icon>
            </i>
          </div>
        </form>
      </div>

      <div class="section text-center pr-loading-below-search mt-2" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
      </div>

      <div class="listed-detail mt-3" v-if="!loading && candidates.length === 0">
        <h3 class="text-center mt-2 text-primary">No Candidates Found</h3>
      </div>

      <div class="pr-card-gap" v-if="!loading && candidates.length"></div>

      <div class="card pr-results-card" v-if="!loading && candidates.length">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-2 pr-results-head">
            <div class="pr-results-title">Candidates</div>
          </div>

          <!-- Pagination summary (separate row for clean mobile view) -->
          <div class="pr-results-meta mb-2">
            <span class="pr-pill">
              <ion-icon name="people-outline"></ion-icon>
              <b>{{ total }}</b>&nbsp;total
            </span>
            <span class="pr-pill">
              <ion-icon name="layers-outline"></ion-icon>
              <b>{{ currentPage }}</b>/<b>{{ lastPage }}</b>
            </span>
          </div>

          <!-- Mobile-first list cards -->
          <div class="pr-cards d-md-none">
            <div
              v-for="(c, idx) in candidates"
              :key="c.id"
              class="pr-card"
            >
              <div class="pr-card-index">{{ rowNumber(idx) }}</div>
              <div class="pr-card-body">
                <div class="pr-card-name">{{ c.name }}</div>
                <div class="pr-card-meta">
                  <span class="pr-card-meta-item">
                    <ion-icon name="call-outline"></ion-icon>
                    <span>{{ c.mobile || "-" }}</span>
                  </span>
                  <span class="pr-card-dot">•</span>
                  <span class="pr-card-meta-item">
                    <ion-icon name="school-outline"></ion-icon>
                    <span>{{ c.madrasa_name || "-" }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Table (desktop / tablet) -->
          <div class="table-responsive pr-table d-none d-md-block">
            <table class="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style="width: 54px;">#</th>
                  <th>Name</th>
                  <th style="width: 150px;">Mobile</th>
                  <th>Madrasa</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, idx) in candidates" :key="c.id">
                  <td class="text-muted">{{ rowNumber(idx) }}</td>
                  <td>
                    <div class="fw-semibold">{{ c.name }}</div>
                    <div class="small text-muted">ID: {{ c.id }}</div>
                  </td>
                  <td>
                    <a class="text-decoration-none" :href="`tel:${(c.mobile || '').replace(/\\s+/g,'')}`">
                      {{ c.mobile || "-" }}
                    </a>
                  </td>
                  <td>{{ c.madrasa_name || "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination-row mt-2" v-if="lastPage > 1">
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage === 1" @click="fetchCandidates(currentPage - 1)">
              <ion-icon name="chevron-back-outline"></ion-icon> Prev
            </button>
            <span class="text-muted small">{{ currentPage }} / {{ lastPage }}</span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage === lastPage" @click="fetchCandidates(currentPage + 1)">
              Next <ion-icon name="chevron-forward-outline"></ion-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PeaceRadioService from "../../services/peace-radio.service";

export default {
  name: "PeaceRadioCourses",
  data() {
    return {
      loading: false,
      errorMessage: "",
      searchTimer: null,
      filtersOpen: false,

      districts: [],
      zones: [],
      units: [],
      courses: [],
      madrasas: [],

      zoneLoading: false,
      unitLoading: false,
      madrasaLoading: false,

      candidates: [],
      total: 0,
      currentPage: 1,
      lastPage: 1,

      searchQuery: "",
      /** Matches API `per_page`; updated from each response for correct row numbering */
      perPage: 20,

      draftFilters: {
        course_master_id: "",
        district_id: "",
        zone_id: "",
        unit_id: "",
        madrasa_id: "",
      },

      appliedFilters: {
        course_master_id: "",
        district_id: "",
        zone_id: "",
        unit_id: "",
        madrasa_id: "",
      },
    };
  },
  computed: {
    currentUser() {
      return this.$store?.state?.auth?.user || null;
    },
    userLevel() {
      const lvl = this.currentUser?.user_level;
      return lvl == null ? null : Number(lvl);
    },
    showDistrictSelect() {
      // State admins can choose district
      return this.userLevel === 1;
    },
    showZoneSelect() {
      // State + district admins can choose zone
      return this.userLevel === 1 || this.userLevel === 2;
    },
    showUnitSelect() {
      // State + district + zone admins can choose unit
      // Unit admins are already fixed to one unit, so no need to show unit selector
      return this.userLevel === 1 || this.userLevel === 2 || this.userLevel === 3;
    },
  },
  mounted() {
    this.$root.updateParent("Peace Radio Courses");
    this.bootstrap();
  },
  methods: {
    async initScopeDefaults() {
      // Fix location scope based on logged in admin level.
      // We assume `district_id/zone_id/unit_id` in currentUser match Guide DB location IDs used by Peace Radio APIs.
      if (!this.currentUser || !this.userLevel) return;

      if (this.userLevel === 2) {
        // District admin: lock district to user's district
        this.draftFilters.district_id = this.currentUser.district_id || "";
        this.draftFilters.zone_id = "";
        this.draftFilters.unit_id = "";
        this.appliedFilters = { ...this.appliedFilters, ...this.draftFilters };

        if (this.draftFilters.district_id) {
          this.zoneLoading = true;
          try {
            const res = await PeaceRadioService.getZones(this.draftFilters.district_id);
            this.zones = res.data?.status ? res.data.data || [] : [];
          } catch {
            this.zones = [];
          } finally {
            this.zoneLoading = false;
          }
        }
      } else if (this.userLevel === 3) {
        // Zone admin: lock district+zone, allow unit selection inside zone
        this.draftFilters.district_id = this.currentUser.district_id || "";
        this.draftFilters.zone_id = this.currentUser.zone_id || "";
        this.draftFilters.unit_id = "";
        this.appliedFilters = { ...this.appliedFilters, ...this.draftFilters };

        if (this.draftFilters.zone_id) {
          this.unitLoading = true;
          try {
            const res = await PeaceRadioService.getUnits(this.draftFilters.zone_id);
            this.units = res.data?.status ? res.data.data || [] : [];
          } catch {
            this.units = [];
          } finally {
            this.unitLoading = false;
          }
        }
      } else if (this.userLevel === 4) {
        // Unit admin: lock district+zone+unit; no location dropdowns needed
        this.draftFilters.district_id = this.currentUser.district_id || "";
        this.draftFilters.zone_id = this.currentUser.zone_id || "";
        this.draftFilters.unit_id = this.currentUser.unit_id || "";
        this.appliedFilters = { ...this.appliedFilters, ...this.draftFilters };
      }
    },

    async bootstrap() {
      this.errorMessage = "";
      try {
        const [districtRes, courseRes] = await Promise.all([
          PeaceRadioService.getDistricts(),
          PeaceRadioService.getCourses(),
        ]);

        if (districtRes.data?.status) this.districts = districtRes.data.data || [];
        if (courseRes.data?.status) this.courses = courseRes.data.data || [];

        await this.initScopeDefaults();
        await this.fetchMadrasas();
        await this.fetchCandidates(1);
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e?.message || "Failed to load data";
      }
    },

    rowNumber(idx) {
      const per = Number(this.perPage || 20);
      return (this.currentPage - 1) * per + (idx + 1);
    },

    onSearch() {
      clearTimeout(this.searchTimer);
      // Search is live (like other list pages) but debounced.
      // Dropdown filters remain Apply-driven via `appliedFilters`.
      this.searchTimer = setTimeout(() => this.fetchCandidates(1), 350);
    },

    async onDistrictChange() {
      this.draftFilters.zone_id = "";
      this.draftFilters.unit_id = "";
      this.draftFilters.madrasa_id = "";
      this.zones = [];
      this.units = [];

      if (this.draftFilters.district_id) {
        this.zoneLoading = true;
        try {
          const res = await PeaceRadioService.getZones(this.draftFilters.district_id);
          this.zones = res.data?.status ? res.data.data || [] : [];
        } catch {
          this.zones = [];
        } finally {
          this.zoneLoading = false;
        }
      }

      await this.fetchMadrasas();
    },

    async onZoneChange() {
      this.draftFilters.unit_id = "";
      this.draftFilters.madrasa_id = "";
      this.units = [];

      if (this.draftFilters.zone_id) {
        this.unitLoading = true;
        try {
          const res = await PeaceRadioService.getUnits(this.draftFilters.zone_id);
          this.units = res.data?.status ? res.data.data || [] : [];
        } catch {
          this.units = [];
        } finally {
          this.unitLoading = false;
        }
      }

      await this.fetchMadrasas();
    },

    async onUnitChange() {
      this.draftFilters.madrasa_id = "";
      await this.fetchMadrasas();
    },

    async fetchMadrasas() {
      this.madrasaLoading = true;
      try {
        const res = await PeaceRadioService.getMadrasas({
          district_id: this.draftFilters.district_id,
          zone_id: this.draftFilters.zone_id,
          unit_id: this.draftFilters.unit_id,
        });
        this.madrasas = res.data?.status ? res.data.data || [] : [];
      } catch {
        this.madrasas = [];
      } finally {
        this.madrasaLoading = false;
      }
    },

    async applyFilters() {
      this.appliedFilters = { ...this.draftFilters };
      await this.fetchCandidates(1);
      this.filtersOpen = false;
    },

    async fetchCandidates(page = 1) {
      this.loading = true;
      this.errorMessage = "";
      try {
        const res = await PeaceRadioService.getCandidates({
          page: page,
          per_page: this.perPage,
          search: this.searchQuery,
          course_master_id: this.appliedFilters.course_master_id,
          district_id: this.appliedFilters.district_id,
          zone_id: this.appliedFilters.zone_id,
          unit_id: this.appliedFilters.unit_id,
          madrasa_id: this.appliedFilters.madrasa_id,
        });

        const payload = res.data?.data;
        if (res.data?.status && payload) {
          this.candidates = payload.data || [];
          this.total = payload.total || 0;
          this.currentPage = payload.current_page || page;
          this.lastPage = payload.last_page || 1;
          if (payload.per_page) {
            this.perPage = Number(payload.per_page);
          }
        } else {
          this.candidates = [];
          this.total = 0;
          this.currentPage = 1;
          this.lastPage = 1;
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e?.message || "Failed to load candidates";
        this.candidates = [];
      } finally {
        this.loading = false;
      }
    },

    async resetFilters() {
      this.searchQuery = "";
      this.perPage = 20;
      this.draftFilters = {
        course_master_id: "",
        district_id: "",
        zone_id: "",
        unit_id: "",
        madrasa_id: "",
      };
      this.appliedFilters = {
        course_master_id: "",
        district_id: "",
        zone_id: "",
        unit_id: "",
        madrasa_id: "",
      };
      this.zones = [];
      this.units = [];
      await this.fetchMadrasas();
      await this.fetchCandidates(1);
    },
  },
};
</script>

<style scoped>
.pr-courses-page {
  padding-bottom: 16px;
  --page-pad: 16px;
}
.page-container {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  padding: 0 var(--page-pad);
}

.pr-filter-card {
  border: 1px solid #eef0f3;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.pr-filter-card :deep(.card-body) {
  padding: 12px 12px;
}
.pr-filter-header {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  cursor: pointer;
}
.pr-filter-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: #0f172a;
}
.pr-filter-title { flex: 1 1 auto; }
.pr-filter-title ion-icon { color: #1a418c; font-size: 18px; }
.pr-filter-meta {
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.pr-filter-chevron {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e8edf5;
  background: #f8fafc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1a418c;
  transition: transform 0.18s ease;
}
.pr-filter-chevron.open {
  transform: rotate(180deg);
}
.pr-filter-collapse {
  margin-top: 6px;
}
.pr-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid #e8edf5;
  background: #f8fafc;
  color: #334155;
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
}
.pr-pill ion-icon { color: #1a418c; font-size: 13px; }
.pr-filter-grid :deep(.form-control),
.pr-filter-grid :deep(.form-select) {
  border-radius: 12px;
}
.pr-filter-grid :deep(.input-wrapper) {
  position: static;
}
.pr-filter-grid :deep(label.label) {
  position: static;
  display: block;
  margin: 0 0 6px 0;
  transform: none;
  line-height: 1.2;
}

.pr-search-outside {
  margin-top: 10px;
  padding: 0;
  width: 100%;
}
.pr-search-outside#search {
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.pr-search-outside :deep(form.search-form) {
  margin: 0 !important;
  padding: 0 !important;
}
.pr-search-outside :deep(.searchbox) {
  margin-bottom: 0;
  width: 100%;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.pr-search-outside :deep(.search-form) {
  width: 100%;
}
.pr-search-outside :deep(.form-control) {
  width: 100%;
  max-width: none;
  display: block;
}
.pr-actions {
  margin-top: 2px;
}
.pr-filter-footer {
  padding: 10px 12px;
  border-top: 1px solid #eef0f3;
  background: #fff;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.pr-filter-footer .btn {
  min-height: 44px;
  border-radius: 12px;
  font-weight: 700;
}

.pr-card-gap {
  height: 8px;
}
.pr-results-card {
  border: 1px solid #eef0f3;
  border-radius: 12px;
  box-shadow: none;
}
.pr-results-card :deep(.card-body) {
  padding: 10px;
}
.pr-results-head {
  gap: 10px;
  margin-bottom: 8px !important;
}
.pr-results-title {
  font-weight: 800;
  font-size: 14px;
  color: #0f172a;
}
.pr-results-meta {
  display: flex;
  gap: 6px;
  margin-bottom: 8px !important;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
}

.pr-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pr-card {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 9px;
}
.pr-card { cursor: default; }
.pr-card-index {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: #eef2ff;
  color: #1a418c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
}
.pr-card-body {
  flex: 1 1 auto;
  min-width: 0;
}
.pr-card-name {
  font-weight: 700;
  font-size: 13.5px;
  color: #0f172a;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pr-card-meta {
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 11.5px;
  line-height: 1.25;
}
.pr-card-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.pr-card-meta-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pr-card-meta ion-icon {
  color: #94a3b8;
  font-size: 13px;
  flex: 0 0 auto;
}
.pr-card-dot {
  color: #cbd5e1;
  flex: 0 0 auto;
}

.pr-table thead th {
  font-size: 12px;
  color: #6c757d;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

@media (max-width: 420px) {
  .pr-courses-page { --page-pad: 8px; }
  .pr-filter-header { flex-direction: row; align-items: center; }
}
</style>

